import { useState, useEffect } from 'react'

import {
  useGetNearbyAdventuresQuery,
  useGetNearbyAdventuresLazyQuery,
} from 'hooks/useQueries'

import TrackVisibility from '../../../components/utils/TrackVisibility'
import { HeadingXl } from '../../../components/display/Typography'

import SuggestedAdventures from '../SuggestedAdventures'

interface NearbyAdventuresProps {
  adventureId: string
  // When true the query is SSR-seeded: it runs non-lazily and reads the seed on
  // the server + first client render so the cards land in the server HTML. Other
  // callers leave it false and keep the lazy, scroll-triggered behaviour.
  prefetched?: boolean
}

const NearbyAdventures = ({
  adventureId,
  prefetched = false,
}: NearbyAdventuresProps) => {
  const [visible, setVisible] = useState(false)

  // Prefetched path: non-lazy + cache-first so it reads the SSR seed on the
  // server and the first client render with loading=false.
  const prefetchedQuery = useGetNearbyAdventuresQuery({
    variables: { id: adventureId },
    fetchPolicy: 'cache-first',
    skip: !prefetched,
  })

  const [loadAdventures, lazy] = useGetNearbyAdventuresLazyQuery()

  useEffect(() => {
    if (!prefetched && visible) {
      loadAdventures({ variables: { id: adventureId } })
    }
  }, [prefetched, visible, adventureId])

  const called = prefetched ? true : lazy.called
  const loading = prefetched ? prefetchedQuery.loading : lazy.loading
  const data = prefetched ? prefetchedQuery.data : lazy.data
  const fetchMore = prefetched ? prefetchedQuery.fetchMore : lazy.fetchMore

  const notYetFetched = loading || !called

  const handleFetchMore = () => {
    fetchMore({
      variables: {
        cursor: data?.adventure?.nearbyAdventures?.pageInfo?.endCursor,
      },
    })
  }

  const canFetchMore = data?.adventure?.nearbyAdventures?.pageInfo?.hasNextPage
  const adventures = data?.adventure?.nearbyAdventures

  const content = (
    <SuggestedAdventures
      adventures={adventures}
      inItineraryContext={false}
      canFetchMore={canFetchMore}
      loading={notYetFetched}
      handleFetchMore={handleFetchMore}
      maxSlidesDesktop={3}
      customTitle={<HeadingXl>More Things To Do Nearby</HeadingXl>}
    />
  )

  // Prefetched renders the seeded cards directly. Otherwise keep the visibility
  // wrapper that triggers the lazy fetch on scroll.
  return prefetched ? (
    content
  ) : (
    <TrackVisibility
      onChange={(isVisible) => {
        if (isVisible) setVisible(true)
      }}
    >
      {content}
    </TrackVisibility>
  )
}

export default NearbyAdventures
